import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { PublicLayout } from '../layouts/PublicLayout';
import { authService } from '../services/api/authService';
import type { InviteVerifyResponse } from '../types';

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [isVerifying, setIsVerifying] = useState(true);
  const [verifyResult, setVerifyResult] = useState<InviteVerifyResponse | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setVerifyResult({ valid: false, message: '招待トークンがありません。' });
        setIsVerifying(false);
        return;
      }

      try {
        const result = await authService.verifyInviteToken(token);
        setVerifyResult(result);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '招待の検証中にエラーが発生しました。';
        setVerifyResult({ valid: false, message: errorMessage });
      } finally {
        setIsVerifying(false);
      }
    };

    verify();
  }, [token]);

  const validateForm = (): boolean => {
    let isValid = true;

    // Name validation
    if (!name.trim()) {
      setNameError('氏名を入力してください');
      isValid = false;
    } else if (name.length < 1 || name.length > 50) {
      setNameError('氏名は1〜50文字で入力してください');
      isValid = false;
    } else {
      setNameError(null);
    }

    // Password validation
    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;
    if (!password) {
      setPasswordError('パスワードを入力してください');
      isValid = false;
    } else if (!passwordRegex.test(password)) {
      setPasswordError('8文字以上、英数字混在で入力してください');
      isValid = false;
    } else {
      setPasswordError(null);
    }

    // Confirm password validation
    if (!confirmPassword) {
      setConfirmPasswordError('確認用パスワードを入力してください');
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError('パスワードが一致しません');
      isValid = false;
    } else {
      setConfirmPasswordError(null);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm() || !token) {
      return;
    }

    setIsSubmitting(true);

    try {
      await authService.createAccount({
        token,
        name: name.trim(),
        password,
      });

      navigate('/login', {
        state: { message: 'アカウントが作成されました。ログインしてください。' }
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'エラーが発生しました';
      // ユーザーに分かりやすいエラーメッセージに変換
      if (errorMessage.includes('expired') || errorMessage.includes('期限')) {
        setError('招待リンクの有効期限が切れています。管理者に再度招待を依頼してください。');
      } else if (errorMessage.includes('invalid') || errorMessage.includes('無効')) {
        setError('無効な招待リンクです。管理者に再度招待を依頼してください。');
      } else if (errorMessage.includes('already') || errorMessage.includes('既に')) {
        setError('このメールアドレスは既に登録されています。ログインページからログインしてください。');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <PublicLayout title="招待を確認中" maxWidth="xs">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress color="primary" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            招待トークンを検証しています...
          </Typography>
        </Box>
      </PublicLayout>
    );
  }

  // Invalid token state
  if (!verifyResult?.valid) {
    return (
      <PublicLayout title="招待が無効です" maxWidth="xs">
        <Alert severity="error" sx={{ mb: 3 }}>
          {verifyResult?.message || 'この招待リンクは無効または期限切れです。管理者に再度招待を依頼してください。'}
        </Alert>
        <Button
          component={RouterLink}
          to="/login"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
        >
          ログインページへ
        </Button>
      </PublicLayout>
    );
  }

  // Account creation form
  return (
    <PublicLayout title="アカウント作成" maxWidth="xs">
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          招待メールアドレス
        </Typography>
        <TextField
          value={verifyResult.email}
          disabled
          fullWidth
          size="small"
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'text.secondary',
            },
          }}
        />
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          label="氏名"
          placeholder="例: 山田 太郎"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!nameError}
          helperText={nameError || '1〜50文字'}
          fullWidth
          required
          sx={{ mb: 2 }}
        />

        <TextField
          label="パスワード"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!passwordError}
          helperText={passwordError || '8文字以上、英数字混在'}
          fullWidth
          required
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="パスワード（確認）"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={!!confirmPasswordError}
          helperText={confirmPasswordError}
          fullWidth
          required
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  size="small"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'アカウントを作成'}
        </Button>
      </Box>
    </PublicLayout>
  );
}
